import { Box } from "@chakra-ui/react";

import { RanaRasterColorSettings } from "@/lib/rana-raster-details/components/rana-raster-color-settings";
import { useFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { RasterFileDescriptor } from "@/types/descriptor-types";
import { getLegendUnit } from "@/utils/get-legend-unit";

import { CompleteFile } from "../../../types/file-reference-with-file";
import { RasterLayer } from "../../../types/publication";
import {
  applyRdcStyling,
  useConfigureRasterMinMax,
  useRasterStylingOptions,
} from "../../map-layers";
import { LEGEND_ITEM_GAP } from "../consts/legend-item-gap";

import { LegendLabel } from "./legend-label";
import { RasterPhysicalQuantityDropdown } from "./raster-physical-quantity-dropdown";

export function RasterLegend({ layer, file }: { layer: RasterLayer; file: CompleteFile }) {
  // Fetch raster data and styling information
  const fileDescriptor = file.file.descriptor;
  const rasterData = useRasterStylingOptions(fileDescriptor, layer);
  const rasterDescriptor =
    fileDescriptor.status?.id === "completed"
      ? (fileDescriptor as RasterFileDescriptor)
      : undefined;
  const { data: raster } = useFileDescriptorViewQuery(fileDescriptor.id, "lizard-raster");

  // Manage style overrides and get final styles
  const { setDefaultColor, setColorRange, setColorBounds, setOpacity } = useConfigureRasterMinMax({
    layer,
    rasterDescriptor,
    raster,
  });

  // Early returns for loading/missing data
  if (!rasterData || !rasterDescriptor) return null;
  const { styles } = rasterData;

  const unit = getLegendUnit(file.file.descriptor);

  // Show interactive color settings for continuous colormaps
  return (
    <Box mb={LEGEND_ITEM_GAP}>
      <RanaRasterColorSettings
        label={<LegendLabel layer={layer} file={file} />}
        descriptor={rasterDescriptor}
        layer={rasterData.wmsLayer}
        styles={applyRdcStyling(styles, layer.properties.styleId)}
        unit={unit}
        opacity={layer.opacity}
        setOpacity={setOpacity}
        setDefaultColor={setDefaultColor}
        setColorRange={setColorRange}
        setColorBounds={setColorBounds}
      >
        <RasterPhysicalQuantityDropdown layer={layer} />
      </RanaRasterColorSettings>
    </Box>
  );
}
